import React, { useState, useEffect, useCallback } from 'react';
import { BarChart3, PieChart as PieIcon, X, Sparkles, TrendingUp } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PALETTE = ['#2FAE8F', '#5DADE2', '#BB8FCE', '#D6A84F', '#F1948A', '#4ECDC4', '#F0B27A', '#FF6B6B'];

function stripPrefix(n) {
  return n ? n.replace(/^(منطقة |المنطقة )/, '') : n;
}

const MiniTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="fa-tooltip">
      <span className="fa-tooltip-name">{d.name}</span>
      <span className="fa-tooltip-val">{d.count || d.value}</span>
    </div>
  );
};

const MapAnalyticsFloating = ({ filters = {}, isOpen, onOpen, onClose, hidden }) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const t = (ar, en) => isArabic ? ar : en;
  const [stats, setStats] = useState(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setAnimate(false);
      setTimeout(() => setAnimate(true), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!API_URL) return;
    const params = new URLSearchParams();
    if (filters.regionId) params.set('region_id', filters.regionId);
    if (filters.cityId) params.set('city_id', filters.cityId);
    if (filters.categoryId) params.set('category_id', filters.categoryId);
    if (filters.subcategoryId) params.set('subcategory_id', filters.subcategoryId);
    if (filters.search) params.set('search', filters.search);
    fetch(`${API_URL}/api/projects/stats?${params}`)
      .then(r => r.json())
      .then(data => {
        setStats(data);
        setAnimate(false);
        setTimeout(() => setAnimate(true), 200);
      })
      .catch(console.error);
  }, [filters]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleOpen = useCallback(() => {
    onOpen();
    setAnimate(false);
    setTimeout(() => setAnimate(true), 300);
  }, [onOpen]);

  if (!stats) return null;

  const topRegions = (stats.top_regions || []).map((r, i) => ({
    name: stripPrefix(isArabic ? r.name_ar : r.name_en),
    count: r.count,
    fill: PALETTE[i % PALETTE.length],
  }));

  const catData = (stats.by_category || []).map((c, i) => ({
    name: isArabic ? c.name_ar : c.name_en,
    value: c.count,
    fill: PALETTE[i % PALETTE.length],
  }));

  const catTotal = catData.reduce((s, c) => s + c.value, 0);

  return (
    <>
      {/* Trigger button */}
      {!isOpen && !hidden && (
        <button className="an-trigger" data-testid="analytics-trigger" onClick={handleOpen}>
          <TrendingUp size={16} />
          <span>{t('التحليلات', 'Analytics')}</span>
        </button>
      )}

      {/* Side panel */}
      {isOpen && (
        <>
          <div className="an-backdrop-dim" data-testid="analytics-panel"
            onClick={onClose} />
          <div className="an-panel" data-testid="analytics-panel-content">

            {/* Header */}
            <div className="an-top-bar">
              <div className="an-title-area">
                <Sparkles size={16} className="so2-sparkle" />
                <h1 className="an-title">{t('تحليلات المشاريع', 'Project Analytics')}</h1>
              </div>
              <button className="so3-close" data-testid="analytics-panel-close" onClick={onClose}>
                <X size={16} />
              </button>
            </div>

            {/* Total badge */}
            <div className="an-scope">
              <span className="an-scope-label">
                {t('إجمالي المشاريع', 'Total Projects')}: {stats.total || 0}
              </span>
            </div>

            {/* Body */}
            <div className="an-body">

              {/* Regions section */}
              <div className={`an-section ${animate ? 'an-section-in' : ''}`}
                data-testid="analytics-regions-section" style={{ '--sec-delay': '0s' }}>
                <div className="an-section-head" style={{ '--sec-color': '#5DADE2' }}>
                  <div className="an-section-icon" style={{ background: 'linear-gradient(135deg,#5DADE2,#2E86C1)' }}>
                    <BarChart3 size={18} color="#fff" />
                  </div>
                  <span className="an-section-title">{t('توزيع المناطق', 'Region Distribution')}</span>
                </div>
                <div className="an-chart-wrap">
                  <ResponsiveContainer width="100%" height={topRegions.length * 36 + 16}>
                    <BarChart data={topRegions} layout="vertical" margin={{ top: 4, right: 8, bottom: 4, left: 8 }}>
                      <XAxis type="number" hide />
                      <YAxis type="category" dataKey="name" width={90} orientation="right"
                        tick={{ fill: 'rgba(245,241,232,0.6)', fontSize: 12, fontFamily: "'Cairo',sans-serif", fontWeight: 600 }}
                        axisLine={false} tickLine={false} />
                      <Tooltip content={<MiniTooltip />} cursor={false} />
                      <Bar dataKey="count" radius={[6, 0, 0, 6]} barSize={16}>
                        {topRegions.map((r, i) => <Cell key={i} fill={r.fill} fillOpacity={0.7} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Categories section */}
              <div className={`an-section ${animate ? 'an-section-in' : ''}`}
                data-testid="analytics-categories-section" style={{ '--sec-delay': '0.15s' }}>
                <div className="an-section-head" style={{ '--sec-color': '#2FAE8F' }}>
                  <div className="an-section-icon" style={{ background: 'linear-gradient(135deg,#2FAE8F,#1F7A63)' }}>
                    <PieIcon size={18} color="#fff" />
                  </div>
                  <span className="an-section-title">{t('توزيع الأنشطة', 'Category Distribution')}</span>
                </div>
                <div className="an-donut-layout">
                  <div className="an-donut-wrap">
                    <ResponsiveContainer width={120} height={120}>
                      <PieChart>
                        <Pie data={catData} dataKey="value" cx="50%" cy="50%"
                          innerRadius={35} outerRadius={52} paddingAngle={2} strokeWidth={0}>
                          {catData.map((c, i) => <Cell key={i} fill={c.fill} fillOpacity={0.75} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="an-donut-center">
                      <span className="an-donut-num">{catTotal}</span>
                      <span className="an-donut-lbl">{t('مشروع', 'projects')}</span>
                    </div>
                  </div>
                  <div className="an-legend">
                    {catData.map((c, i) => (
                      <div key={i} className="an-legend-row">
                        <span className="an-legend-dot" style={{ background: c.fill }} />
                        <span className="an-legend-name">{c.name}</span>
                        <span className="an-legend-val">{c.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MapAnalyticsFloating;
